import { Ban, CheckCircle2, RotateCcw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { ClassificationModal } from '../components/ClassificationModal';
import { repositoryClassificationsApi } from '../classificationsApi';
import type { RepositoryClassificationListItem } from '../repositoryTypes';

type TabKey = 'info';

const BREADCRUMB: BreadcrumbSegment[] = [
  'Document Management',
  { label: 'Repository', to: '/repository' },
  { label: 'Settings', to: '/repository/settings' },
  { label: 'Classifications', to: '/repository/settings/classifications' },
  'Classification',
];

/**
 * One information classification in the catalog.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — a name, its definition, what the rule does, and the trail of who changed
 * it — not a record whose content splits into panels a reader chooses between.
 *
 * Thin, and that is the point. What it carries that a form cannot: how many documents already
 * carry the classification, whether the confidential rule is in force, and who last touched
 * the wording — all things a reader wants before renaming or deleting it.
 */
export function ClassificationProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [classification, setClassification] = useState<RepositoryClassificationListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      repositoryClassificationsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setClassification(next);

          // Cleared on success, or a failed restore would keep its banner over a page that has
          // since re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this classification.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Puts a deleted classification back.
   *
   * **No confirm tick and no password.** Restoring puts something back, and ceremony on an undo
   * only teaches readers to click through ceremony. The server still refuses it when another
   * active row has taken the name meanwhile, and that refusal arrives in the banner.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await repositoryClassificationsApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not restore this classification.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  // The full-page error state is for a failure with NOTHING to show. Where a record is already
  // up it is still the last good answer, so a failed restore goes to the banner instead.
  if (error && !classification) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !classification) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!classification) {
    return null;
  }

  const { documentCount } = classification;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={BREADCRUMB} />

      <ProfileHeaderShell
        id="repository-classification-profile"
        header={
          <ProfileHeader
            id="repository-classification-profile-header"
            icon={<ShieldCheck className="h-6 w-6" aria-hidden="true" />}
            title={classification.name}
            meta={[
              /*
                Spelled out rather than a bare count: a number beside a classification could as
                easily be the folders using it or the people who may read it, and those are
                different questions asked from the same page.
              */
              `${documentCount} ${documentCount === 1 ? 'document carries' : 'documents carry'} it`,
              ...(classification.isConfidential ? ['Confidential'] : []),
            ]}
            status={
              <StatusText tone={classification.isActive ? 'active' : 'inactive'}>
                {classification.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={classification.createdByName}
            createdAt={classification.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="repository-classification-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : classification.isActive ? (
            <ProfileBanner
              id="repository-classification-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This classification is offered: it appears in the Classification picker when a controlled document is registered."
            />
          ) : (
            <ProfileBanner
              id="repository-classification-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This classification is deleted. It is no longer offered when a document is registered, and the documents already carrying it keep resolving its name."
              action={
                // Gates the button, not the banner: a read-only viewer still needs to see the
                // state. `delete` is the key, because restoring is that action's own reversal.
                <PermissionGate permission="repository.delete">
                  <Button
                    id="repository-classification-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="repository-classification-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'repository.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: classification.name },
              { label: 'Definition', value: classification.description },

              /*
                The confidential flag sits on THIS card rather than one of its own: a reader
                came to correct what the row does, and which half of that is a name and which
                is a checkbox is our storage layout, not their question. One card, one modal,
                and this card has the Edit.
              */
              {
                label: 'Confidential',
                value: classification.isConfidential
                  ? 'Yes — a document under it is refused into a folder shared beyond the people explicitly granted it'
                  : 'No — a document under it may sit in a shared folder',
              },
              {
                label: 'Documents carrying it',
                /*
                  A zero is MUTED, like every other quantity in the product: a classification
                  nothing carries is the ordinary case for a newly added one, and the count
                  worth reading is the one that is not zero. Muted, never an em-dash: "nothing
                  carries this" and "we do not know" are different facts.
                */
                value: (
                  <span
                    className={`font-mono tabular-nums ${documentCount === 0 ? 'text-slate-300' : ''}`}
                  >
                    {documentCount}
                  </span>
                ),
              },
              {
                label: 'Order',
                /*
                  The STORED ordering value, under a heading that says so. The matrix reads
                  least to most restricted and the order is gapped by tens, so this is not a
                  position counted from one — that is only correct with the whole ladder in
                  hand, which a single record does not have.
                */
                value: (
                  <span className="font-mono tabular-nums">{classification.sortOrder}</span>
                ),
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="repository-classification-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: classification.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(classification.createdAt)} className="font-mono">
                    {formatDateTime(classification.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: classification.updatedByName },
              {
                label: 'Updated',
                value: classification.updatedAt ? (
                  <time dateTime={toIso(classification.updatedAt)} className="font-mono">
                    {formatDateTime(classification.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <ClassificationModal
          open
          editing={classification}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
